/**
 * RiskHeatmap – D3 band-scale risk matrix visualization.
 * d3-scale provides axis math; rendering uses React SVG JSX (no useEffect, no DOM mutation).
 *
 * @param title        - Chart heading
 * @param subtitle     - Optional subheading
 * @param dataPoints   - Risk items to plot
 * @param onPointClick - Optional handler; falls back to section-scroll behavior
 * @param className    - Optional wrapper class override
 */
import { useState } from 'react';
import { scaleBand } from 'd3-scale';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/Tooltip';
import { AlertTriangle, TrendingUp, Shield, ExternalLink } from 'lucide-react';

export interface HeatmapDataPoint {
  label: string;
  likelihood: 'low' | 'moderate' | 'moderately-high' | 'high';
  impact: 'low' | 'moderate' | 'moderately-high' | 'high';
  description?: string;
  linkedSection?: string;
  linkedVulnerabilityId?: string;
  recommendation?: string;
}

interface RiskHeatmapProps {
  title: string;
  subtitle?: string;
  dataPoints: HeatmapDataPoint[];
  onPointClick?: (point: HeatmapDataPoint) => void;
  className?: string;
}

const AXES = ['low', 'moderate', 'moderately-high', 'high'] as const;
const AXES_INV = [...AXES].reverse() as AxisLevel[];
type AxisLevel = (typeof AXES)[number];

const AXIS_LABELS: Record<AxisLevel, string> = {
  low: 'Low',
  moderate: 'Moderate',
  'moderately-high': 'Moderately High',
  high: 'High',
};

const AXIS_LABELS_FULL: Record<AxisLevel, string> = {
  low: 'Low',
  moderate: 'Moderate',
  'moderately-high': 'Moderately High',
  high: 'High',
};

// HSL zone fill colors per priority level
const ZONE_COLORS: Record<AxisLevel, string> = {
  low: 'hsl(145,65%,42%)',
  moderate: 'hsl(65,85%,45%)',
  'moderately-high': 'hsl(45,95%,55%)',
  high: 'hsl(0,85%,55%)',
};

// Whether dot label text should be dark (true) or white (false)
const POINT_TEXT_DARK: Record<AxisLevel, boolean> = {
  low: false,
  moderate: true,
  'moderately-high': true,
  high: false,
};

const PRIORITY_LABELS: Record<AxisLevel, string> = {
  low: 'Low Priority',
  moderate: 'Moderate Priority',
  'moderately-high': 'Moderately High Priority',
  high: 'High Priority',
};

const PRIORITY_TEXT: Record<AxisLevel, string> = {
  low: 'text-risk-low',
  moderate: 'text-risk-medium',
  'moderately-high': 'text-risk-high',
  high: 'text-risk-critical',
};

const PRIORITY_BG: Record<AxisLevel, string> = {
  low: 'bg-[hsl(var(--risk-low-bg))]',
  moderate: 'bg-[hsl(var(--risk-medium-bg))]',
  'moderately-high': 'bg-[hsl(var(--risk-high-bg))]',
  high: 'bg-[hsl(var(--risk-critical-bg))]',
};

// Circle radius for individual data points
const R = 14;

// Deterministic spiral-grid jitter for up to 20 co-located points.
// Each position is offset so R=14 circles don't overlap (gap ≥ 2px).
const JITTER: [number, number][] = [
  [0, 0],
  [-32, 0],
  [32, 0],
  [0, -32],
  [0, 32],
  [-25, -25],
  [25, -25],
  [-25, 25],
  [25, 25],
  [-38, -16],
  [38, -16],
  [-38, 16],
  [38, 16],
  [-16, -38],
  [16, -38],
  [-16, 38],
  [16, 38],
  [-44, 0],
  [44, 0],
  [0, -44],
];

// SVG layout constants — wider so the inner plot area has room for jitter
const M = { top: 28, right: 28, bottom: 68, left: 120 };
const SVG_W = 620;
const SVG_H = 500;
const IW = SVG_W - M.left - M.right;
const IH = SVG_H - M.top - M.bottom;

// Max dots shown individually per cell before collapsing to a count bubble
const MAX_INDIVIDUAL = 6;

const getPriority = (likelihood: AxisLevel, impact: AxisLevel): AxisLevel => {
  const sum = AXES.indexOf(likelihood) + AXES.indexOf(impact);
  if (sum <= 1) return 'low';
  if (sum <= 3) return 'moderate';
  if (sum <= 5) return 'moderately-high';
  return 'high';
};

const RiskHeatmap = ({
  title,
  subtitle,
  dataPoints,
  onPointClick,
  className,
}: RiskHeatmapProps) => {
  const [filter, setFilter] = useState(
    new Set<AxisLevel>(['low', 'moderate', 'moderately-high', 'high'])
  );

  const toggleFilter = (p: AxisLevel) =>
    setFilter((prev) => {
      const next = new Set(prev);
      if (next.has(p)) {
        next.delete(p);
      } else {
        next.add(p);
      }
      return next;
    });

  // D3 band scales — pure math, no DOM mutation
  const xScale = scaleBand<AxisLevel>()
    .domain([...AXES])
    .range([0, IW])
    .padding(0.1);
  const yScale = scaleBand<AxisLevel>().domain(AXES_INV).range([0, IH]).padding(0.1);
  const bw = xScale.bandwidth();
  const bh = yScale.bandwidth();

  const visible = dataPoints.filter((p) => filter.has(getPriority(p.likelihood, p.impact)));

  // Group by cell to apply deterministic jitter per index
  const cellGroups = new Map<string, HeatmapDataPoint[]>();
  visible.forEach((p) => {
    const k = `${p.likelihood}|${p.impact}`;
    if (!cellGroups.has(k)) cellGroups.set(k, []);
    cellGroups.get(k)!.push(p);
  });

  const handleClick = (p: HeatmapDataPoint) => {
    if (onPointClick) {
      onPointClick(p);
      return;
    }
    if (!p.linkedSection) return;
    window.dispatchEvent(
      new CustomEvent('expandSection', { detail: { sectionId: p.linkedSection } })
    );
    if (p.linkedVulnerabilityId) {
      window.dispatchEvent(
        new CustomEvent('expandVulnerability', {
          detail: { vulnerabilityId: p.linkedVulnerabilityId },
        })
      );
    }
    setTimeout(
      () =>
        document
          .getElementById(p.linkedSection!)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      100
    );
  };

  return (
    <div className={cn('card-elevated p-6 animate-slide-up', className)}>
      {/* Header + filter */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-bold text-foreground">{title}</h3>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        <div className="flex flex-wrap gap-1">
          {AXES.map((ax) => (
            <button
              key={ax}
              onClick={() => toggleFilter(ax)}
              className={cn(
                'px-2 py-1 text-xs rounded-md border transition-all',
                filter.has(ax)
                  ? 'border-transparent'
                  : 'bg-muted text-muted-foreground border-border opacity-50'
              )}
              style={
                filter.has(ax)
                  ? {
                      backgroundColor: ZONE_COLORS[ax],
                      color: POINT_TEXT_DARK[ax] ? '#1a1a1a' : 'white',
                    }
                  : undefined
              }
            >
              {AXIS_LABELS[ax]}
            </button>
          ))}
        </div>
      </div>

      {/* D3 band-scale SVG chart */}
      <svg
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        width="100%"
        className="overflow-visible"
        aria-label="Risk heatmap"
      >
        <g transform={`translate(${M.left},${M.top})`}>
          {/* Background risk-zone cells */}
          {AXES.map((li) =>
            AXES.map((ii) => (
              <rect
                key={`z-${li}-${ii}`}
                x={xScale(li)!}
                y={yScale(ii)!}
                width={bw}
                height={bh}
                rx={6}
                fill={ZONE_COLORS[getPriority(li, ii)]}
                opacity={0.14}
              />
            ))
          )}

          {/* Subtle grid lines */}
          {AXES.map((ax) => (
            <g key={`gl-${ax}`}>
              <line
                x1={xScale(ax)! + bw / 2}
                y1={0}
                x2={xScale(ax)! + bw / 2}
                y2={IH}
                stroke="currentColor"
                strokeOpacity={0.07}
              />
              <line
                x1={0}
                y1={yScale(ax)! + bh / 2}
                x2={IW}
                y2={yScale(ax)! + bh / 2}
                stroke="currentColor"
                strokeOpacity={0.07}
              />
            </g>
          ))}

          {/* X-axis: Likelihood */}
          {AXES.map((ax) => (
            <text
              key={`xt-${ax}`}
              x={xScale(ax)! + bw / 2}
              y={IH + 22}
              textAnchor="middle"
              fontSize={12}
              fontWeight={500}
              fill="currentColor"
              opacity={0.85}
            >
              {AXIS_LABELS[ax]}
            </text>
          ))}
          <text
            x={IW / 2}
            y={IH + 54}
            textAnchor="middle"
            fontSize={13}
            fontWeight={700}
            fill="currentColor"
            opacity={0.9}
          >
            Likelihood
          </text>

          {/* Y-axis: Impact (inverted — high at top) */}
          {AXES_INV.map((ax) => (
            <text
              key={`yt-${ax}`}
              x={-12}
              y={yScale(ax)! + bh / 2 + 4}
              textAnchor="end"
              fontSize={12}
              fontWeight={500}
              fill="currentColor"
              opacity={0.85}
            >
              {AXIS_LABELS[ax]}
            </text>
          ))}
          <text
            transform={`translate(${-M.left + 14},${IH / 2}) rotate(-90)`}
            textAnchor="middle"
            fontSize={13}
            fontWeight={700}
            fill="currentColor"
            opacity={0.9}
          >
            Impact
          </text>

          {/* Data point circles with Radix tooltips */}
          {Array.from(cellGroups.entries()).flatMap(([key, pts]) => {
            const priority = getPriority(pts[0].likelihood, pts[0].impact);
            const color = ZONE_COLORS[priority];
            const dark = POINT_TEXT_DARK[priority];
            const textFill = dark ? '#1a1a1a' : 'white';
            const overflow = pts.length > MAX_INDIVIDUAL;
            // When there are more points than we can spread, show first N-1 individually
            // and replace the last slot with a count bubble
            const shown = overflow ? pts.slice(0, MAX_INDIVIDUAL - 1) : pts;
            const overflowCount = pts.length - shown.length;

            const renderDot = (
              p: HeatmapDataPoint,
              i: number,
              isCount?: boolean,
              count?: number
            ) => {
              const [ox, oy] = JITTER[Math.min(i, JITTER.length - 1)];
              const cx = xScale(p.likelihood)! + bw / 2 + ox;
              const cy = yScale(p.impact)! + bh / 2 + oy;
              const isClickable = !!onPointClick || !!p.linkedSection;
              // Truncate label to fit inside R=14 circle: ≤6 chars comfortably
              const short = p.label.length > 6 ? p.label.slice(0, 5) + '…' : p.label;

              const circleNode = (
                <g
                  transform={`translate(${cx},${cy})`}
                  onClick={() => handleClick(p)}
                  style={{ cursor: isClickable ? 'pointer' : 'default' }}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  aria-label={isCount ? `${count} more items` : p.label}
                  onKeyDown={isClickable ? (e) => e.key === 'Enter' && handleClick(p) : undefined}
                >
                  <circle
                    r={R}
                    fill={color}
                    stroke="white"
                    strokeWidth={1.5}
                    opacity={isCount ? 0.7 : 0.92}
                  />
                  <text
                    textAnchor="middle"
                    fontSize={isCount ? 10 : 8}
                    fontWeight={700}
                    fill={isCount ? textFill : textFill}
                    dominantBaseline="middle"
                    style={{ userSelect: 'none' }}
                  >
                    {isCount ? `+${count}` : short}
                  </text>
                </g>
              );

              if (isCount) return <g key={`${key}-overflow`}>{circleNode}</g>;

              return (
                <Tooltip key={`${key}-${i}`}>
                  <TooltipTrigger asChild>{circleNode}</TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-xs p-0 overflow-hidden"
                    sideOffset={8}
                  >
                    <div className={cn('px-3 py-2', PRIORITY_BG[priority])}>
                      <div className="flex items-center gap-2">
                        {priority === 'high' && (
                          <AlertTriangle className="w-4 h-4 text-risk-critical" />
                        )}
                        {priority === 'moderately-high' && (
                          <AlertTriangle className="w-4 h-4 text-risk-high" />
                        )}
                        {priority === 'moderate' && (
                          <TrendingUp className="w-4 h-4 text-risk-medium" />
                        )}
                        {priority === 'low' && <Shield className="w-4 h-4 text-risk-low" />}
                        <span className={cn('font-semibold text-sm', PRIORITY_TEXT[priority])}>
                          {PRIORITY_LABELS[priority]}
                        </span>
                      </div>
                    </div>
                    <div className="p-3 space-y-2">
                      <h4 className="font-semibold text-foreground">{p.label}</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Likelihood:</span>
                          <span className="ml-1 font-medium">{AXIS_LABELS_FULL[p.likelihood]}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Impact:</span>
                          <span className="ml-1 font-medium">{AXIS_LABELS_FULL[p.impact]}</span>
                        </div>
                      </div>
                      {p.description && (
                        <p className="text-xs text-muted-foreground border-t pt-2">
                          {p.description}
                        </p>
                      )}
                      {p.recommendation && (
                        <div className="text-xs border-t pt-2">
                          <span className="font-medium text-foreground">Recommendation: </span>
                          <span className="text-muted-foreground">{p.recommendation}</span>
                        </div>
                      )}
                      {isClickable && (
                        <div className="flex items-center gap-1 text-xs text-primary pt-1 border-t">
                          <ExternalLink className="w-3 h-3" />
                          <span>Click to view details</span>
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            };

            return [
              ...shown.map((p, i) => renderDot(p, i)),
              ...(overflow
                ? [renderDot(shown[shown.length - 1], MAX_INDIVIDUAL - 1, true, overflowCount + 1)]
                : []),
            ];
          })}
        </g>
      </svg>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 pt-4 border-t border-border">
        {AXES.map((ax) => (
          <div key={ax} className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: ZONE_COLORS[ax] }} />
            <span className="text-xs text-muted-foreground">{PRIORITY_LABELS[ax]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RiskHeatmap;
